import { HydrationBoundary, type DehydratedState } from '@tanstack/react-query';
import { useAtomValue, useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import { memo, useEffect, useMemo, useState } from 'react';
import { jsonLdScriptProps } from 'react-schemaorg';
import { debugSortScoreVersionAtom, type SortScoreVersion } from '@/atoms/typesenseDebug';
import { useQueryState } from '@/hooks/state/useQueryState';
import { FreeFirstSfxSearchResults } from '@/components/FreeFirstSfxSearchResults';
import { SEO } from '@/components/SEO';
import { ShowMoreAssetsHome } from '@/components/ShowMoreAssetsHome';
import TypesenseFilters from '@/components/TypesenseFilters';
import { ASSET_TYPES } from '@/components/TypesenseFilters/assetTypes';
import { VirtualizedTrackList } from '@/components/VirtualizedTrackList';
import { SHOW_MORE_VISITOR_PAGE_SIZE } from '@/constants/showMoreExperiment';
import { useSfxSearchWithQueue } from '@/hooks/search';
import { useShowFreeFirst } from '@/hooks/search/shared/useShowFreeFirst';
import { useListingInfiniteScrollActive } from '@/hooks/useListingInfiniteScrollActive';
import { useShowMoreVariant } from '@/hooks/useShowMoreVariant';
import { useSfxResultsWithContributorSlugs } from '@/hooks/useSfxResultsWithContributorSlugs';
import { useSchema } from '@/hooks/seo/useSchema';
import useSEO from '@/hooks/seo/useSEO';
import { useTypesenseFilterParams } from '@/hooks/useTypesenseFilterParams';
import { getPageNumberFromSlug } from '@/utils/pages';
import { getBreadcrumbListSchema } from '@/utils/schema/get-breadcrumb-list-schema';
import { getSfxSchema } from '@/utils/schema/get-sfx-schema';
import { replaceTemplateVariables } from '@/utils/template-variables';
import { normalizeSfxDocumentForSearch } from '@/utils/track-documents';
import type { SearchResponseHit, SfxDocument, SortOption } from '@music-vine/setlist';
import type { Collection } from '@/types/collection';
import type { TrackUrl } from '@/types/tracks';
import type { CategoryHeader } from '@/utils/pages/api';
import type { NextPage } from 'next';
import Head from 'next/head';
import type { BreadcrumbList, CollectionPage } from 'schema-dts';
import type { Category } from '@/api/comprehensivelistings';
import { getPrimaryHeading } from '@/components/PageHeader/utils';
import { Header, HeaderBreadcrumbs, HeaderDescription, HeaderImage, HeaderRelatedSearches, HeaderSubtitle, HeaderTicks, HeaderTileCarousel, HeaderTileCarouselDebug, HeaderTitle } from '@/components/ui/header';
import { HeaderAmbiguousLink } from '@/components/ui/header/ambiguous-link';
import type { RelatedSearchItem } from '@/components/ui/header/related-searches';
import { SFX_STYLE_PAGE_TICKS } from '@/components/ui/header/ticks';
import { useIsLoggedIn } from '@/hooks/auth';
import { getBreadcrumbs } from '@/lib/breadcrumbs/get-breadcrumbs';
import { isCollection } from '@/lib/page-is-collection';
import { useTypesenseCarouselsForPage } from '@/hooks/useTypesenseCarousels';
import { carouselDebugReadoutAtom, overrideCarouselSearchAtom } from '@/atoms/debug';
import { AUTO_CAROUSEL_MINIMUM_SFX_TILES_TO_SHOW } from '@music-vine/setlist';
import { getSectionCopy } from '@/utils/show-free-first/sectionCopy';
export type BrowseTagPageParams = {
  categorySlug: string[];
};
export type BrowseTagPaginatedPageParams = BrowseTagPageParams & {
  pageNumber: string;
};
export interface SfxPageProps {
  authState?: string;
  comprehensiveListings?: Category[] | null;
  contextPath: string;
  dehydratedState?: DehydratedState;
  pageData: CategoryHeader | Collection;
  pageTitle?: string;
  totalCount?: number;
}
const SFX_CANONICAL_PARENT_ORDER = 999;

// Inputs resolved once by the parent so only the active layout's search hooks run.
interface SfxResultsChildProps {
  contextPath: string;
  filters: string;
  pageData: CategoryHeader | Collection;
  query: string;
  sort: SortOption;
  sortScoreVersion: SortScoreVersion;
}

// Default single-stream layout (free-first OFF): infinite scroll + "show more" gate + SEO schema.
const DefaultSearchResults = ({
  query,
  filters,
  sort,
  sortScoreVersion,
  contextPath,
  pageData
}: SfxResultsChildProps) => {
  const showMoreVariant = useShowMoreVariant();
  const isLoggedIn = useIsLoggedIn();

  // Group B visitors get one page of SHOW_MORE_VISITOR_PAGE_SIZE assets and
  // must click "Show more" to engage infinite scroll (DEV-5440). Group A and
  // logged-in users keep auto infinite scroll (current behaviour).
  const isShowMoreGated = showMoreVariant === 'B' && !isLoggedIn;
  const [infiniteEnabled, setInfiniteEnabled] = useState(false);

  // Re-arm the gate whenever the underlying query changes so each distinct
  // result set starts as a single page rather than inheriting the previous
  // query's infinite mode.
  useEffect(() => {
    setInfiniteEnabled(false);
  }, [filters, sort, query]);

  // Set up SFX search with filters
  const {
    data: sfxData,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isError,
    isFetchingNextPage,
    loadMoreRef
  } = useSfxSearchWithQueue({
    query,
    filters,
    sort,
    sortScoreVersion,
    fetchFreeAndPremium: true,
    autoSetupQueue: true,
    contextSlug: contextPath,
    contextPath,
    perPage: isShowMoreGated ? SHOW_MORE_VISITOR_PAGE_SIZE : undefined
  });
  const infiniteScrollAllowed = !isShowMoreGated || infiniteEnabled;

  // Hide the site footer while committed to the result stream, reveal it at
  // the true end (group B; group A hides the footer on these routes anyway).
  useListingInfiniteScrollActive(infiniteScrollAllowed && !!hasNextPage);

  // Extract and transform search results
  const sfxResults = useMemo(() => {
    if (!sfxData?.results) {
      return [];
    }
    return sfxData.results;
  }, [sfxData]);

  // Infinite scroll is now handled by the loadMoreRef from useSfxSearchWithQueue
  // No need for separate useInView hook or useEffect

  // Schema data for SEO - convert Typesense documents to expected Track format
  const sfxForSchema = sfxResults.map(hit => ({
    id: Number(hit.document.sfx_variant_id),
    // Use variant ID as unique identifier
    name: hit.document.name || '',
    slug: hit.document.sfx_slug || '',
    current_context: {
      id: Number(hit.document.sfx_variant_id)
    },
    is_explicit_lyrics: false,
    is_premium: hit.document.is_premium ?? false,
    is_sfx: true,
    artist: {
      id: 0,
      // Default ID for schema purposes
      name: hit.document.contributor_name || '',
      slug: '' // SFX doesn't have contributor_slug
    },
    track_version: [{
      id: Number(hit.document.sfx_variant_id),
      // Use variant ID here too
      version_length: hit.document.version_length || 0,
      version_precise_length: hit.document.version_precise_length || hit.document.version_length || 0
    }] as [TrackUrl]
  }));
  const schema = useSchema<CollectionPage>({
    '@type': 'CollectionPage',
    name: isCollection(pageData) ? pageData.name : pageData?.primary_heading_visitor ?? pageData?.name,
    description: pageData?.schema_description ?? pageData?.description ?? undefined,
    mainEntity: {
      '@type': 'ItemList',
      numberOfItems: sfxResults.length,
      itemListElement: getSfxSchema(sfxForSchema)
    }
  });
  return <>
      <Head data-sentry-element="Head" data-sentry-source-file="index.tsx">
        {!!pageData && <script type="application/ld+json" {...jsonLdScriptProps<CollectionPage>(schema)} key={`sfx-jsonld-${isCollection(pageData) ? pageData.name : pageData.name_sentence_case}`} />}
      </Head>
      <SearchResults fetchNextPage={infiniteScrollAllowed ? fetchNextPage : undefined} hasNextPage={infiniteScrollAllowed && hasNextPage} isError={isError} isFetchingNextPage={isFetchingNextPage} isLoading={isLoading} results={sfxResults} data-sentry-element="SearchResults" data-sentry-source-file="index.tsx" />
      {isShowMoreGated && !infiniteEnabled && !!hasNextPage && <ShowMoreAssetsHome className="mt-4" isFetchingNextPage={isFetchingNextPage} onClick={() => setInfiniteEnabled(true)} />}
      {/* Loading trigger for infinite scroll */}
      {infiniteScrollAllowed && !!hasNextPage && <div className="h-20" ref={loadMoreRef} />}
    </>;
};

// Resolves shared inputs once, then renders free-first split or default layout — never both.
const SearchResultsWithFilters = ({
  fullSlug,
  pageData
}: {
  fullSlug: string;
  pageData: CategoryHeader | Collection;
}) => {
  // Slug is not passed as a filter here — it's handled by the contextSlug parameter in the search hooks.
  const {
    filter: combinedFilters,
    search: urlSearchTerms
  } = useTypesenseFilterParams();
  const debugSortScoreVersion = useAtomValue(debugSortScoreVersionAtom);
  const [showFreeFirst] = useShowFreeFirst();

  // Use useQueryState to read sort parameter consistently with TypesenseSort components
  const isValidSort = (sort: unknown): sort is SortOption => {
    const validSorts = ['relevancy', 'latest', 'undiscovered'];
    return typeof sort === 'string' && validSorts.includes(sort);
  };
  const [currentSort] = useQueryState('sort', {
    defaultValue: 'relevancy' as SortOption,
    parse: value => isValidSort(value) ? value : 'relevancy',
    serialize: value => value
  });
  const searchQuery = Array.isArray(urlSearchTerms) ? urlSearchTerms.join(' ').trim() : urlSearchTerms;
  const childProps: SfxResultsChildProps = {
    query: searchQuery,
    filters: combinedFilters,
    sort: currentSort,
    sortScoreVersion: debugSortScoreVersion,
    contextPath: fullSlug,
    pageData
  };
  return showFreeFirst ? <FreeFirstSfxSearchResults contextPath={fullSlug} contextSlug={fullSlug} filters={combinedFilters} premiumTitle={getSectionCopy({
    assetType: 'sfx',
    pageType: isCollection(pageData) ? 'collection' : 'category'
  }).premiumHeading} query={searchQuery} sort={currentSort} sortScoreVersion={debugSortScoreVersion} data-sentry-element="FreeFirstSfxSearchResults" data-sentry-component="SearchResultsWithFilters" data-sentry-source-file="index.tsx" /> : <DefaultSearchResults {...childProps} data-sentry-element="DefaultSearchResults" data-sentry-component="SearchResultsWithFilters" data-sentry-source-file="index.tsx" />;
};

// SearchResults component using virtualization for SFX
const SearchResults = memo(({
  results,
  isLoading,
  isError,
  hasNextPage,
  fetchNextPage,
  isFetchingNextPage
}: {
  results: SearchResponseHit<SfxDocument>[];
  isLoading: boolean;
  isError: boolean;
  hasNextPage?: boolean;
  fetchNextPage?: () => void;
  isFetchingNextPage?: boolean;
}) => {
  // Convert SFX results to the format expected by VirtualizedTrackList
  const convertedResults = useMemo(() => results.map(hit => normalizeSfxDocumentForSearch({
    ...hit.document,
    asset_type: 'sfx',
    sfx_variant_id: Number(hit.document.sfx_variant_id)
  })), [results]);

  // sfx.v3 docs don't ship contributor_slug — resolve every visible
  // contributor's slug from contributors.v1 so each row's contributor name
  // becomes a clickable link.
  const enrichedResults = useSfxResultsWithContributorSlugs(convertedResults);
  return <VirtualizedTrackList fetchNextPage={fetchNextPage} hasNextPage={hasNextPage} initialRect={{
    width: 800,
    // Estimate based on container width
    height: 600 // Reasonable viewport height for SSR
  }} isError={isError} isFetchingNextPage={isFetchingNextPage} isLoading={isLoading} results={enrichedResults} />;
});
SearchResults.displayName = 'SearchResults';
const BrowseSfxPage: NextPage<SfxPageProps> = memo(({
  pageData,
  dehydratedState,
  contextPath,
  pageTitle: pageTitleServer,
  totalCount,
  authState
}) => {
  const router = useRouter();
  const overrideCarouselSearch = useAtomValue(overrideCarouselSearchAtom);
  const setOverrideCarouselSearchState = useSetAtom(overrideCarouselSearchAtom);
  const setCarouselDebugReadoutAtomState = useSetAtom(carouselDebugReadoutAtom);
  // Category routes expose `categorySlug`; the shared collection routes expose
  // `collectionSlug`. Collections never paginate, but read both so pagination
  // stays correct wherever this component is mounted.
  const {
    pageNumber
  } = getPageNumberFromSlug(router.query.categorySlug ?? router.query.collectionSlug);
  const {
    generateTitleFromExistingTitle
  } = useSEO();
  const userIsLoggedIn = useIsLoggedIn();
  let displayName = pageData.name;
  if (!isCollection(pageData) && pageData.type === 'tag') {
    displayName = `${pageData.name} sound effects`;
  }
  const meta_title = generateTitleFromExistingTitle({
    existingTitle: pageData?.meta_title ?? undefined,
    fallbackTitle: isCollection(pageData) ? `${displayName} • Free Sound Effects For Creators ` : `Free ${displayName}`,
    pageNumber: pageNumber ? Number(pageNumber) : undefined,
    excludeTagline: true
  });
  let meta_description = replaceTemplateVariables(pageData?.meta_description ?? pageData?.description ?? undefined, {
    count: totalCount
  });
  let description = replaceTemplateVariables(pageData?.description ?? pageData?.short_description ?? undefined, {
    count: totalCount
  });
  const overrideCarouselExcludes = isCollection(pageData) ? '' : pageData.excluded_keywords ?? '';
  useEffect(() => {
    setOverrideCarouselSearchState(prev => ({
      ...prev,
      overrideCarouselExcludes
    }));
  }, [setOverrideCarouselSearchState, overrideCarouselExcludes]);

  // Use the contextPath from getServerSideProps which handles collection vs category URLs correctly
  const fullSlug = contextPath;

  // Define facets for SFX filters
  const sfxFacets = useMemo(() => [{
    value: 'type',
    label: 'Classification',
    display: 'toggle' as const
  }, {
    value: 'intensity',
    label: 'Intensity',
    display: 'popover' as const
  }, {
    value: 'proximity',
    label: 'Proximity',
    display: 'popover' as const
  }, {
    value: 'quantity',
    label: 'Quantity',
    display: 'popover' as const,
    separator: true
  }], []);
  const sfxBreadcrumbs = useMemo(() => {
    const result = getBreadcrumbs({
      data: pageData,
      assetType: 'sfx'
    });
    return result;
  }, [pageData]);

  // Update description for tags if not provided
  if (!(description || isCollection(pageData)) && pageData.type === 'tag' && pageData?.name_sentence_case) {
    description = `Free ${pageData?.name_sentence_case} sound effects. Download the best royalty free ${pageData?.name_sentence_case} sound effects and audio clips for your content. Safe for YouTube, TikTok, podcasts, social media and more!`;
  }
  if (!meta_description) {
    meta_description = description;
  }

  // After rebrand
  const sfxRelatedSearches = useMemo(() => {
    let result: (string | RelatedSearchItem)[] = [];
    if (isCollection(pageData)) {
      return result;
    }

    // NOTE: Pick only those with show_in_related_search === true and those with order different from 999
    // to prevent duplicate items in the related searches
    const filteredTaxonomy = pageData?.taxonomy.filter(item => item.order !== SFX_CANONICAL_PARENT_ORDER && item.show_in_related_search === true);
    result = filteredTaxonomy.map(taxonomy => ({
      label: taxonomy.name,
      href: taxonomy.url
    }));
    return result;
  }, [pageData]);
  const pageTitleClient = useMemo(() => getPrimaryHeading({
    data: pageData,
    userIsAuthenticated: userIsLoggedIn
  }), [pageData, userIsLoggedIn]);
  const showSeoTicks = useMemo(() => {
    const isPersonalized = 'pageType' in pageData && pageData.pageType === 'personalized';

    // Roles only restore after mount, so without the server's authState logged-in HTML would ship ticks and then drop them
    const isAuthenticated = userIsLoggedIn || authState === 'authenticated';
    return !(isAuthenticated || isPersonalized);
  }, [userIsLoggedIn, authState, pageData]);
  const seoTickContent = useMemo(() => {
    if (isCollection(pageData)) {
      return;
    }
    if (pageData?.target_ksp) {
      return [pageData.target_ksp, 'No copyright issues', 'Download now'];
    }
    return SFX_STYLE_PAGE_TICKS;
  }, [pageData]);
  const pageIsCollection = isCollection(pageData);
  const ambiguousLinks = useMemo(() => {
    if (pageIsCollection) {
      return [];
    }
    return pageData?.ambiguous_links ?? [];
  }, [pageData, pageIsCollection]);
  const carouselQueryOptions = {
    enabled: !isCollection(pageData) && !!fullSlug
  };
  const {
    data: processedCarouselResult
  } = useTypesenseCarouselsForPage({
    library: 'sfx',
    pageData,
    queryOptions: carouselQueryOptions,
    carouselSearchDebugOverride: overrideCarouselSearch
  });
  const pageCarousels = processedCarouselResult?.carousels;
  if (processedCarouselResult?.debug_info) {
    setCarouselDebugReadoutAtomState(processedCarouselResult?.debug_info);
  }
  const ShowCarouselDescription = useMemo(() => {
    if (isCollection(pageData) || !pageCarousels) {
      return false;
    }
    let showCarouselDescription = true;
    for (const pageCarousel of pageCarousels) {
      // If any of the carousels have descriptions set to hidden, don't show descriptions
      if (!(pageCarousel.show_carousel_description || overrideCarouselSearch.carouselDebugInfoVisible)) {
        showCarouselDescription = false;
      }
    }
    return showCarouselDescription;
  }, [pageCarousels, overrideCarouselSearch.carouselDebugInfoVisible, pageData.slug, pageData]);
  const SliderCarouselData = useMemo(() => {
    if (isCollection(pageData) || !pageCarousels) {
      return [];
    }
    const result = pageCarousels.flatMap(item => item.tiles).map(item => ({
      id: String(item.slug),
      name: item.title,
      order: item.order,
      tile_image: item.image,
      url: item.slug,
      description: item.description ?? undefined,
      debug_info: item.debug_info
    }));
    return result;
  }, [pageCarousels, pageData.slug, pageData]);

  // Removed: window.scrollTo({ top: 0 }) was overriding native scroll
  // restoration on back navigation. Next.js handles scroll-to-top on
  // forward navigation automatically.

  const headerImageSrc = isCollection(pageData) ? pageData.thumbnail_image?.image_aws_url : pageData.tile_image;
  const PAGE_TITLE = pageTitleServer ?? pageTitleClient;
  return <HydrationBoundary state={dehydratedState}>
        <SEO description={meta_description} title={meta_title} />
        {!!sfxBreadcrumbs && <Head>
            <script type="application/ld+json" {...jsonLdScriptProps<BreadcrumbList>(getBreadcrumbListSchema(sfxBreadcrumbs))} key={`breadcrumb-jsonld-${pageData.slug}`} />
          </Head>}
        <Header separator ticks={showSeoTicks}>
          {isCollection(pageData) && headerImageSrc && <HeaderImage src={headerImageSrc} alt={`${pageData?.inspired_by ? 'Inspired by ' : ''}${PAGE_TITLE} cover image`} containerClassName="hidden md:block" />}
          <HeaderTitle>{PAGE_TITLE}</HeaderTitle>
          <HeaderDescription>{description}</HeaderDescription>
          {!!sfxBreadcrumbs && <HeaderBreadcrumbs {...sfxBreadcrumbs} />}
          {!!sfxRelatedSearches?.length && <HeaderRelatedSearches searches={sfxRelatedSearches} />}
          {showSeoTicks && <HeaderTicks content={seoTickContent} />}
          {ambiguousLinks && <HeaderSubtitle>
              {ambiguousLinks.map(link => <HeaderAmbiguousLink data={link} key={link.url} />)}
            </HeaderSubtitle>}
          {!!overrideCarouselSearch.carouselDebugInfoVisible && <HeaderTileCarouselDebug items={SliderCarouselData} showDescriptions={ShowCarouselDescription} minimumTilesToShow={AUTO_CAROUSEL_MINIMUM_SFX_TILES_TO_SHOW} />}
          {!pageIsCollection && SliderCarouselData.length > 0 && <HeaderTileCarousel items={SliderCarouselData} showDescriptions minimumTilesToShow={AUTO_CAROUSEL_MINIMUM_SFX_TILES_TO_SHOW} mode={ShowCarouselDescription ? 'full' : 'title-only'} />}
        </Header>
        {/* SFX filters and track listing with jotai context */}
        <TypesenseFilters assetType={ASSET_TYPES.SFX} facets={sfxFacets} isSticky={true} pageSlug={fullSlug} showSearch={true} showSort={true} sortRelevanceLabelOverride={isCollection(pageData) ? 'As curated' : undefined} stickyContainerClassName="top-[51px] md:top-[67px]">
          <div className="mx-auto w-full space-y-4">
            <SearchResultsWithFilters fullSlug={fullSlug} pageData={pageData} />
          </div>
        </TypesenseFilters>
        {/* 21/10/25 - James is doing a crawl to see how this removal affects SEO, may be permanent in the future - SB */}
        {/* {comprehensiveListings && (
          <ComprehensiveListings
            initialData={comprehensiveListings}
            variant="sfx"
            onlyNewUsers
            showAllButton
          />
         )} */}
      </HydrationBoundary>;
});
BrowseSfxPage.displayName = 'BrowseSfxPage';
export default BrowseSfxPage;